import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectCateringCartFilters, selectCateringCartPackages } from "../../redux/features/restaurants/restaurantSelectors";
import { setCateringCartFilters, setPackagePax } from "../../redux/features/restaurants/restaurantSlice";
import { canDecreasePax } from "../../utils/paxCalculations";
import PaxDecreaseWarningModal from "../modal/PaxDecreaseWarningModal";

const PaxSelector = ({ disabled = false, selectedPackage = null, isAdditionalService = false }) => {
  const dispatch = useDispatch();
  const mainFilters = useSelector(selectCateringCartFilters);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningData, setWarningData] = useState(null);
  
  // Get min and max PAX from package, fallback to defaults
  const minPax = selectedPackage?.min_pax ? parseInt(selectedPackage.min_pax) : 10;
  const maxPax = selectedPackage?.max_pax ? parseInt(selectedPackage.max_pax) : 100;
  
  // For additional services, get PAX from the package itself
  // For main package, use global filters
  let currentPax;
  if (isAdditionalService && selectedPackage) {
    // Find the package in cart to get its PAX value
    const packageInCart = cateringCartPackages.find(pkg => pkg.id === selectedPackage.id);
    currentPax = packageInCart?.pax ? Math.max(packageInCart.pax, minPax) : minPax;
  } else {
    currentPax = mainFilters.pax ? Math.max(mainFilters.pax, minPax) : minPax;
  }
  
  const increment = 5;
  
  console.log('🎯 PaxSelector - State:', {
    'disabled': disabled,
    'currentPax': currentPax,
    'minPax': minPax,
    'maxPax': maxPax,
    'isAdditionalService': isAdditionalService,
    'selectedPackageId': selectedPackage?.id
  });
  
  // Initialize PAX to minPax when package loads or changes
  useEffect(() => {
    if (selectedPackage) {
      if (isAdditionalService) {
        // For additional services, check if package is in cart and has PAX set
        const packageInCart = cateringCartPackages.find(pkg => pkg.id === selectedPackage.id);
        if (packageInCart) {
          const packagePax = packageInCart.pax || minPax;
          // If PAX is below minPax or above maxPax, adjust it
          if (packagePax < minPax) {
            console.log(`🎯 Initializing additional service PAX to minPax: ${minPax} (current: ${packagePax})`);
            dispatch(setPackagePax({ packageId: selectedPackage.id, pax: minPax }));
          } else if (packagePax > maxPax) {
            console.log(`🎯 Capping additional service PAX to maxPax: ${maxPax} (current: ${packagePax})`);
            dispatch(setPackagePax({ packageId: selectedPackage.id, pax: maxPax }));
          }
        }
      } else {
        // For main package, use global filters
        if (!mainFilters.pax || mainFilters.pax < minPax) {
          console.log(`🎯 Initializing main package PAX to minPax: ${minPax} (current: ${mainFilters.pax})`);
          dispatch(setCateringCartFilters({ ...mainFilters, pax: minPax }));
        } else if (mainFilters.pax > maxPax) {
          console.log(`🎯 Capping main package PAX to maxPax: ${maxPax} (current: ${mainFilters.pax})`);
          dispatch(setCateringCartFilters({ ...mainFilters, pax: maxPax }));
        }
      }
    }
  }, [selectedPackage?.id, minPax, maxPax, isAdditionalService, cateringCartPackages, mainFilters, dispatch]);

  const handleIncreasePax = () => {
    if (disabled) return;
    const newPax = Math.min(currentPax + increment, maxPax);
    
    if (isAdditionalService && selectedPackage) {
      dispatch(setPackagePax({ packageId: selectedPackage.id, pax: newPax }));
    } else {
      dispatch(setCateringCartFilters({ ...mainFilters, pax: newPax }));
    }
  };

  const handleDecreasePax = () => {
    if (disabled) return;
    const newPax = Math.max(currentPax - increment, minPax);
    
    // Get the package to check (either from cart or selected package)
    let packageToCheck = null;
    if (isAdditionalService && selectedPackage) {
      packageToCheck = cateringCartPackages.find(pkg => pkg.id === selectedPackage.id);
    } else {
      // For main package, check the first non-additional-service package
      packageToCheck = cateringCartPackages.find(pkg => !pkg.isAdditionalService);
    }
    
    // Validate if PAX can be decreased (only if package has items)
    if (packageToCheck && packageToCheck.items && packageToCheck.items.length > 0) {
      const validation = canDecreasePax(packageToCheck, currentPax, newPax);
      
      console.log('🔍 PAX Decrease Validation:', {
        packageId: packageToCheck.id,
        packageName: packageToCheck.name_en,
        currentPax,
        newPax,
        canDecrease: validation.canDecrease,
        categoriesExceeded: validation.categoriesExceeded
      });
      
      if (!validation.canDecrease) {
        // Show warning modal with exceeded categories
        setWarningData({
          categoriesExceeded: validation.categoriesExceeded,
          currentPax,
          targetPax: newPax
        });
        setShowWarningModal(true);
        return; // Prevent PAX decrease
      }
    }
    
    // Proceed with PAX decrease if validation passed or no items in cart
    if (isAdditionalService && selectedPackage) {
      dispatch(setPackagePax({ packageId: selectedPackage.id, pax: newPax }));
    } else {
      dispatch(setCateringCartFilters({ ...mainFilters, pax: newPax }));
    }
  };

  // Check if package has a valid PAX range (handle empty strings, null, undefined)
  // This check is done after all hooks to comply with React rules
  const minPaxValue = selectedPackage?.min_pax;
  const maxPaxValue = selectedPackage?.max_pax;
  const hasValidPaxRange = minPaxValue && maxPaxValue && 
    minPaxValue !== "" && maxPaxValue !== "" &&
    parseInt(minPaxValue) > 0 && parseInt(maxPaxValue) > 0;
  
  // If no valid PAX range for additional services, don't render the component
  if (isAdditionalService && !hasValidPaxRange) {
    console.log('🎯 PaxSelector - Hidden (no valid PAX range for additional service):', {
      packageName: selectedPackage?.name_en || selectedPackage?.title,
      min_pax: minPaxValue,
      max_pax: maxPaxValue
    });
    return null;
  }

  return (
    <>
      <div className={`flex items-center gap-4 ${disabled ? 'opacity-50' : ''}`}>
        <span className="font-bold text-lg text-gray-400">PAX</span>
        <div className="flex items-center border border-gray-300 rounded-lg">
          <button
            onClick={handleDecreasePax}
            disabled={disabled || currentPax <= minPax}
            className={`px-3 py-2 text-lg font-bold ${
              disabled || currentPax <= minPax
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            -
          </button>
          <div className="px-4 py-2 text-lg font-bold text-gray-700 min-w-[60px] text-center border-x border-gray-300">
            {currentPax}
          </div>
          <button
            onClick={handleIncreasePax}
            disabled={disabled || currentPax >= maxPax}
            className={`px-3 py-2 text-lg font-bold ${
              disabled || currentPax >= maxPax
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            +
          </button>
        </div>
      </div>
      
      {/* Warning Modal */}
      {showWarningModal && warningData && (
        <PaxDecreaseWarningModal
          categoriesExceeded={warningData.categoriesExceeded}
          currentPax={warningData.currentPax}
          targetPax={warningData.targetPax}
          onClose={() => {
            setShowWarningModal(false);
            setWarningData(null);
          }}
        />
      )}
    </>
  );
};

export default PaxSelector;
